import React, { useEffect, useRef, useState } from 'react';
import { PhoneCall, Target, Wrench, Megaphone, BarChart2, ArrowUpRight } from 'lucide-react';

const HowItWorks = () => {
  const [visibleItems, setVisibleItems] = useState([]);
  const sectionRef = useRef(null);
  const itemRefs = useRef([]);

  useEffect(() => {
    const headerObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisibleItems(prev => [...new Set([...prev, 'header'])]);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) headerObserver.observe(sectionRef.current);

    const itemObservers = itemRefs.current.map((ref, index) => {
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              setVisibleItems(prev => [...new Set([...prev, index])]);
            }, index * 120);
          }
        },
        { threshold: 0.15 }
      );
      if (ref) obs.observe(ref);
      return obs;
    });

    return () => {
      headerObserver.disconnect();
      itemObservers.forEach(o => o.disconnect());
    };
  }, []);

  const steps = [
    {
      icon: PhoneCall,
      title: "Discovery Call",
      description: "We analyze your audience, content, and goals to identify the biggest monetization opportunities unique to your brand.",
      tag: "Week 1"
    },
    {
      icon: Target,
      title: "Strategy Session",
      description: "We design a custom digital product roadmap tailored to your audience's needs and your content style.",
      tag: "Week 2"
    },
    {
      icon: Wrench,
      title: "Product Creation",
      description: "We create, package, and polish your digital product — from copy and design to tech and delivery systems.",
      tag: "Weeks 3–6"
    },
    {
      icon: Megaphone,
      title: "Launch Planning",
      description: "We craft a high-impact launch strategy, with pre-launch content, email sequences, and rollout timelines.",
      tag: "Week 7"
    },
    {
      icon: BarChart2,
      title: "Execute & Optimize",
      description: "We launch, measure results, and run continuous A/B tests to improve conversions and audience targeting.",
      tag: "Ongoing"
    },
    {
      icon: ArrowUpRight,
      title: "Scale & Repeat",
      description: "Once proven, we expand your product suite — building a full ecosystem of passive income streams.",
      tag: "Month 3+"
    }
  ];

  const isHeaderVisible = visibleItems.includes('header');

  return (
    <section id="how-it-works" ref={sectionRef} className="py-24 px-6 bg-[rgb(17,17,19)] relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[rgba(197,155,47,0.05)] blur-[130px] rounded-full pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[rgba(124,56,178,0.04)] blur-[100px] rounded-full pointer-events-none"></div>

      <div className="max-w-[900px] mx-auto relative z-10">
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-700 ${
          isHeaderVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="inline-flex items-center space-x-2 text-[rgb(197,155,47)] text-sm font-semibold mb-4">
            <div className="w-8 h-[2px] bg-gradient-to-r from-transparent to-[rgb(197,155,47)]"></div>
            <span>THE PROCESS</span>
            <div className="w-8 h-[2px] bg-gradient-to-l from-transparent to-[rgb(197,155,47)]"></div>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            How It <span className="bg-gradient-to-r from-[rgb(197,155,47)] to-[rgb(224,194,95)] text-transparent bg-clip-text">Works</span>
          </h2>
          <p className="text-xl text-[rgb(218,218,218)] max-w-[700px] mx-auto">
            A proven 6-step system to turn your audience into consistent revenue
          </p>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical Line — desktop only, sits in center column */}
          <div className="absolute hidden sm:block top-0 bottom-0 bg-gradient-to-b from-[rgb(197,155,47)] via-[rgba(124,56,178,0.5)] to-transparent w-[2px]"
            style={{ left: 'calc(50% - 1px)' }}
          ></div>

          <div className="space-y-10">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isLeft = index % 2 === 0;
              const isItemVisible = visibleItems.includes(index);

              return (
                <div
                  key={index}
                  ref={el => (itemRefs.current[index] = el)}
                  className={`relative transition-all ease-out ${
                    isItemVisible ? 'opacity-100 translate-x-0' : isLeft ? 'opacity-0 -translate-x-12' : 'opacity-0 translate-x-12'
                  }`}
                  style={{ transitionDuration: '600ms' }}
                >
                  {/* Mobile: single column layout */}
                  <div className="flex items-start gap-5 sm:hidden">
                    <div className="relative flex-shrink-0">
                      <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[rgb(197,155,47)] to-[rgb(124,56,178)] flex items-center justify-center shadow-[0_0_20px_rgba(197,155,47,0.4)]">
                        <Icon size={22} className="text-white" />
                      </div>
                      <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[rgb(17,17,19)] border-2 border-[rgb(197,155,47)] flex items-center justify-center">
                        <span className="text-[9px] text-[rgb(197,155,47)] font-bold">{index + 1}</span>
                      </div>
                    </div>
                    <div className="flex-1 bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.08)] rounded-2xl p-5 hover:border-[rgb(197,155,47)] transition-all duration-300 group">
                      <span className="text-xs font-semibold text-[rgb(124,56,178)] uppercase tracking-wider mb-1 block">{step.tag}</span>
                      <h3 className="text-lg font-bold text-white mb-2 group-hover:text-[rgb(197,155,47)] transition-colors duration-300">{step.title}</h3>
                      <p className="text-[rgb(161,161,170)] text-sm leading-relaxed">{step.description}</p>
                    </div>
                  </div>

                  {/* Desktop: CSS Grid — 3 columns: left card | center icon | right card */}
                  <div className="hidden sm:grid grid-cols-[1fr_80px_1fr] items-center gap-0">
                    {/* Left slot */}
                    <div className={`pr-6 ${isLeft ? '' : 'invisible'}`}>
                      {isLeft && (
                        <div className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.08)] rounded-2xl p-6 hover:border-[rgb(197,155,47)] transition-all duration-300 group hover:shadow-[0_10px_40px_rgba(197,155,47,0.15)]">
                          <span className="text-xs font-semibold text-[rgb(124,56,178)] uppercase tracking-wider mb-2 block">{step.tag}</span>
                          <h3 className="text-xl font-bold text-white mb-3 group-hover:text-[rgb(197,155,47)] transition-colors duration-300">{step.title}</h3>
                          <p className="text-[rgb(161,161,170)] leading-relaxed text-sm">{step.description}</p>
                        </div>
                      )}
                    </div>

                    {/* Center icon */}
                    <div className="flex items-center justify-center z-10">
                      <div className="relative w-16 h-16 rounded-full bg-gradient-to-br from-[rgb(197,155,47)] to-[rgb(124,56,178)] flex items-center justify-center shadow-[0_0_24px_rgba(197,155,47,0.5)]">
                        <Icon size={26} className="text-white" />
                        <div className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-[rgb(17,17,19)] border-2 border-[rgb(197,155,47)] flex items-center justify-center">
                          <span className="text-[10px] text-[rgb(197,155,47)] font-bold">{index + 1}</span>
                        </div>
                      </div>
                    </div>

                    {/* Right slot */}
                    <div className={`pl-6 ${!isLeft ? '' : 'invisible'}`}>
                      {!isLeft && (
                        <div className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.08)] rounded-2xl p-6 hover:border-[rgb(197,155,47)] transition-all duration-300 group hover:shadow-[0_10px_40px_rgba(197,155,47,0.15)]">
                          <span className="text-xs font-semibold text-[rgb(124,56,178)] uppercase tracking-wider mb-2 block">{step.tag}</span>
                          <h3 className="text-xl font-bold text-white mb-3 group-hover:text-[rgb(197,155,47)] transition-colors duration-300">{step.title}</h3>
                          <p className="text-[rgb(161,161,170)] leading-relaxed text-sm">{step.description}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;